import {
  CustomFieldEntityEnum,
  DealAmountMappingValue,
  FieldMappingCreateReqType,
  FieldMappingObjectType,
  FieldMappingsResType,
  FieldMappingSystemType,
  FieldMappingType,
  ICustomFieldResSchema,
} from '@monetize/types/app';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { MN_ACC_FIELDS } from '../constants/accountingFieldMappings';
import {
  MN_BLACKLISTED_FIELD_API_NAMES,
  MN_CRM_FIELDS,
  MN_CRM_FIELDS_FOR_HUBSPOT,
  MN_CRM_FIELDS_FOR_SALESFORCE,
} from '../constants/crmFieldMapping';
import { transformAccToFieldMappingBatchUpsertPayloadType } from '../utils/accountingFieldMapping';
import {
  convertCustomFieldTypeToCrmType,
  transformToFieldMappingBatchUpsertPayloadType,
} from '../utils/crmFieldMapping';
import { apiGet, apiGetAllList, apiPost, apiPut } from './axios';
import { fieldMappingServiceQueryKeys } from './queryKeysService';
import { settingsQueryKeys } from './settingsService';

function getFieldConfig(system: string): FieldMappingType {
  switch (system) {
    case 'salesforce':
      return MN_CRM_FIELDS_FOR_SALESFORCE;
    case 'netsuite':
      return MN_ACC_FIELDS;
    case 'hubspot':
      return MN_CRM_FIELDS_FOR_HUBSPOT;
    default:
      return MN_CRM_FIELDS;
  }
}
export const useGetCrmFields = (
  system: FieldMappingSystemType,
  options: Partial<
    UseQueryOptions<FieldMappingType, unknown, FieldMappingType>
  > = {},
) => {
  const url =
    system === 'monetizenow'
      ? '/connector/monetizenow/fields'
      : `/connector/crm/${system}/fields`;

  return useQuery({
    queryKey: fieldMappingServiceQueryKeys.crmFields(system),
    select: (data) => {
      if (system === 'monetizenow') {
        const legalEntityFields =
          data.LegalEntity?.map(({ fieldApiName, ...rest }) => ({
            fieldApiName: `legalEntityId.${fieldApiName}`,
            ...rest,
          })) ?? [];
        const quoteFields = data.Quote?.filter(
          ({ fieldApiName }) =>
            !MN_BLACKLISTED_FIELD_API_NAMES.has(fieldApiName),
        ).concat(legalEntityFields);
        return { ...data, Quote: quoteFields } as FieldMappingType;
      }
      return data;
    },
    queryFn: async () => {
      const { data } = await apiGet<FieldMappingType>(url);
      return data;
    },
    ...options,
    refetchOnWindowFocus: false,
  });
};
export const useGetMonetizeNowAccountingFields = (
  system: FieldMappingSystemType,
) => {
  return useQuery({
    queryKey: fieldMappingServiceQueryKeys.monetizeNowAccountingFields(system),
    queryFn: async () => {
      const { data } = await apiGet<FieldMappingType>(
        `/connector/monetizenow/${system}/fields`,
      );
      return data;
    },
    refetchOnWindowFocus: false,
  });
};

export const useGetMonetizeNowCustomFields = (
  system: FieldMappingSystemType,
) => {
  const { data: monetizeNowFields, isLoading } = useQuery({
    queryKey: [
      ...settingsQueryKeys.customFieldsList(),
      'Account',
      'Contact',
      'Quote',
      'CreditNote',
      'Invoice',
      'Product',
      'Offering',
    ],
    queryFn: async () => {
      const res = await apiGetAllList<ICustomFieldResSchema>(
        '/api/configurations/customFields',
        {
          filters: {
            entity: {
              in: [
                CustomFieldEntityEnum.ACCOUNT,
                CustomFieldEntityEnum.CONTACT,
                CustomFieldEntityEnum.QUOTE,
                CustomFieldEntityEnum.CREDIT_NOTE,
                CustomFieldEntityEnum.INVOICE,
                CustomFieldEntityEnum.PRODUCT,
                CustomFieldEntityEnum.OFFERING,
              ],
            },
          },
        },
      );
      return res;
    },
    refetchOnWindowFocus: false,
    select: (data) => {
      /**
       * Combine custom fields with hard-coded MN fields. Clone the config
       * first — `getFieldConfig` returns shared module-level constants and the
       * reducer below pushes into these arrays, so mutating them directly would
       * leak state across systems/refetches and duplicate custom fields.
       */
      const mnFields = structuredClone(getFieldConfig(system));

      return data?.reduce((result, { displayLabel, entity, key, type }) => {
        const dataType = convertCustomFieldTypeToCrmType(type);
        switch (entity) {
          case CustomFieldEntityEnum.ACCOUNT: {
            const existingField = result.Account?.find(
              ({ fieldApiName }) => fieldApiName === key,
            );
            if (!existingField || existingField.displayLabel !== displayLabel) {
              result.Account?.push({
                fieldApiName: key,
                dataType,
                displayLabel,
                isCustom: true,
              });
            }
            break;
          }
          case CustomFieldEntityEnum.CONTACT: {
            const existingField = result.Contact?.find(
              ({ fieldApiName }) => fieldApiName === key,
            );
            if (!existingField || existingField.displayLabel !== displayLabel) {
              result.Contact?.push({
                fieldApiName: key,
                dataType,
                displayLabel,
                isCustom: true,
              });
            }
            break;
          }
          case CustomFieldEntityEnum.QUOTE: {
            const existingField = result.Quote?.find(
              ({ fieldApiName }) => fieldApiName === key,
            );
            if (!existingField || existingField.displayLabel !== displayLabel) {
              result.Quote?.push({
                fieldApiName: key,
                dataType,
                displayLabel,
                isCustom: true,
              });
            }
            break;
          }
          case CustomFieldEntityEnum.CREDIT_NOTE: {
            const existingField = result.CreditNote?.find(
              ({ fieldApiName }) => fieldApiName === key,
            );

            if (!existingField || existingField.displayLabel !== displayLabel) {
              result.CreditNote?.push({
                fieldApiName: key,
                dataType,
                displayLabel,
                isCustom: true,
              });
            }
            break;
          }
          case CustomFieldEntityEnum.INVOICE: {
            const existingField = result.Invoice?.find(
              ({ fieldApiName }) => fieldApiName === key,
            );
            if (!existingField || existingField.displayLabel !== displayLabel) {
              result.Invoice?.push({
                fieldApiName: key,
                dataType,
                displayLabel,
                isCustom: true,
              });
            }
            break;
          }
          case CustomFieldEntityEnum.PRODUCT: {
            const existingField = result.Product?.find(
              ({ fieldApiName }) => fieldApiName === key,
            );
            if (!existingField || existingField.displayLabel !== displayLabel) {
              result.Product?.push({
                fieldApiName: key,
                dataType,
                displayLabel,
                isCustom: true,
              });
            }
            break;
          }
          case CustomFieldEntityEnum.OFFERING: {
            const existingField = result.Offering?.find(
              ({ fieldApiName }) => fieldApiName === key,
            );
            if (!existingField || existingField.displayLabel !== displayLabel) {
              result.Offering?.push({
                fieldApiName: key,
                dataType,
                displayLabel,
                isCustom: true,
              });
            }
            break;
          }
          default:
            break;
        }
        return result;
      }, mnFields);
    },
  });
  return { isLoading, monetizeNowFields };
};

export const useGetUserDefinedCrmFieldMappings = (
  system: FieldMappingSystemType,
) => {
  return useQuery({
    queryKey: fieldMappingServiceQueryKeys.crmFieldMappings(system),
    queryFn: async () => {
      const { data } = await apiGet<FieldMappingsResType>(
        `/connector/crm/${system}/fieldMappings`,
      );
      return data;
    },
    refetchOnWindowFocus: false,
  });
};

/**
 * `crmToMn` maps CRM fields into MonetizeNow (the default import direction).
 * `mnToCrm` maps MonetizeNow fields out to the CRM (e.g. Product/Offering ->
 * Salesforce Product2).
 */
export type CrmFieldMappingDirection = 'crmToMn' | 'mnToCrm';

const getTransformedPayload = (
  connector: 'accounting' | 'crm',
  connectorSystem: FieldMappingSystemType,
  sourceEntity: FieldMappingObjectType,
  targetEntity: FieldMappingObjectType,
  payloads: FieldMappingCreateReqType[],
  direction: CrmFieldMappingDirection,
) => {
  switch (connector) {
    case 'accounting':
      return transformAccToFieldMappingBatchUpsertPayloadType(
        connectorSystem,
        sourceEntity,
        targetEntity,
        payloads,
      );
    case 'crm': {
      const isMnToCrm = direction === 'mnToCrm';
      const sourceSystem: FieldMappingSystemType = isMnToCrm
        ? 'monetizenow'
        : connectorSystem;
      const targetSystem: FieldMappingSystemType = isMnToCrm
        ? connectorSystem
        : 'monetizenow';
      return transformToFieldMappingBatchUpsertPayloadType(
        sourceSystem,
        sourceEntity,
        targetSystem,
        targetEntity,
        payloads,
      );
    }
    default:
      throw new Error(`Unsupported connector type: ${connector}`);
  }
};

export const useCreateConnectorFieldMapping = (
  targetSystem: FieldMappingSystemType,
  connector: 'accounting' | 'crm',
  options: Partial<
    Omit<
      UseMutationOptions<
        unknown,
        unknown,
        {
          sourceEntity: FieldMappingObjectType;
          targetEntity: FieldMappingObjectType;
          payloads: FieldMappingCreateReqType[];
          direction?: CrmFieldMappingDirection;
        }
      >,
      'onSuccess'
    >
  > = {},
) => {
  const queryClient = useQueryClient();
  return useMutation<
    unknown,
    unknown,
    {
      sourceEntity: FieldMappingObjectType;
      targetEntity: FieldMappingObjectType;
      payloads: FieldMappingCreateReqType[];
      direction?: CrmFieldMappingDirection;
    }
  >({
    mutationFn: async ({
      sourceEntity,
      targetEntity,
      payloads,
      direction = 'crmToMn',
    }) => {
      const transformedPayload = getTransformedPayload(
        connector,
        targetSystem,
        sourceEntity,
        targetEntity,
        payloads,
        direction,
      );

      const { data } = await apiPost(
        `/connector/${connector}/${targetSystem}/fieldMappings`,
        transformedPayload,
      );
      return data;
    },
    onSuccess: (data, payload, ctx) => {
      queryClient.invalidateQueries({
        queryKey:
          fieldMappingServiceQueryKeys.accountingFieldMappings(targetSystem),
      });
      queryClient.invalidateQueries({
        queryKey: fieldMappingServiceQueryKeys.crmFieldMappings(targetSystem),
      });
    },
    ...options,
  });
};

export const useGetAccountingFields = (
  system: FieldMappingSystemType,
  options: Partial<
    UseQueryOptions<FieldMappingType, unknown, FieldMappingType>
  > = {},
) => {
  const url =
    system === 'monetizenow'
      ? '/connector/monetizenow/fields'
      : `/connector/accounting/${system}/fields`;

  return useQuery({
    queryKey: fieldMappingServiceQueryKeys.accountingFields(system),
    select: (data) => {
      return data;
    },
    queryFn: async () => {
      const { data } = await apiGet<FieldMappingType>(url);
      return data;
    },
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useGetUserDefinedAccountingFieldMappings = (
  system: FieldMappingSystemType,
  options: Partial<
    UseQueryOptions<FieldMappingsResType, unknown, FieldMappingsResType>
  > = {},
) => {
  return useQuery({
    queryKey: fieldMappingServiceQueryKeys.accountingFieldMappings(system),
    queryFn: async () => {
      const { data } = await apiGet<FieldMappingsResType>(
        `/connector/accounting/${system}/fieldMappings`,
      );
      return data;
    },
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useSaveDealAmountMapping = (
  options: Partial<
    UseMutationOptions<unknown, unknown, DealAmountMappingValue>
  > = {},
) => {
  const queryClient = useQueryClient();
  return useMutation<unknown, unknown, DealAmountMappingValue>({
    mutationFn: async (dealAmountMapping) => {
      const { data } = await apiPut(
        '/connector/crm/hubspot/dealAmountMapping',
        { value: dealAmountMapping },
      );
      return data;
    },
    onSuccess: (data, variables, ctx) => {
      queryClient.invalidateQueries({
        queryKey: settingsQueryKeys.crm(),
      });
      options.onSuccess?.(data, variables, ctx);
    },
    ...options,
  });
};
